import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingWorkspaceSettings,
  DocumentCraftingWorkspaceSettingsUpdate,
} from "../../../Models/documentCrafting";
import {
  useDocumentCraftingModels,
  useDocumentCraftingWorkspaceSettings,
  useUpdateDocumentCraftingWorkspaceSettings,
} from "../../../Query/useDocumentCrafting";
import { getDocumentCraftingError } from "../../../Api/documentCrafting";
import { ErrorState, LoadingState, Panel } from "../Common/DocumentCraftingUi";

export const ENGLISH_VARIANTS = ["en-GB", "en-US"];
// The server's bounds (settings_service.py: DRAFT_TTL_DAYS_RANGE,
// MAX_UPLOAD_MB_RANGE, PHONE_MAX_LENGTH, WEBSITE_MAX_LENGTH); it validates
// again and names the rejected `field`.
export const DRAFT_TTL_RANGE = { min: 1, max: 90 };
export const MAX_UPLOAD_RANGE = { min: 1, max: 50 };
export const COMPANY_PHONE_MAX = 50;
export const WEBSITE_MAX = 300;

interface FormState {
  default_model_id: number | null;
  company_phone: string;
  website_url: string;
  english_variant: string;
  translation_marker: boolean;
  draft_ttl_days: string;
  max_upload_mb: string;
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

const FORM_FIELDS: (keyof FormState)[] = [
  "default_model_id",
  "company_phone",
  "website_url",
  "english_variant",
  "translation_marker",
  "draft_ttl_days",
  "max_upload_mb",
];

/** The form field a 400 `invalid_request` names, else null. */
export const rejectedField = (error: unknown): keyof FormState | null => {
  const field = getDocumentCraftingError(error)?.field;
  return typeof field === "string" && (FORM_FIELDS as string[]).includes(field)
    ? (field as keyof FormState)
    : null;
};

interface Rejection {
  /** The field the server named; "form" when it named none of them. */
  field: keyof FormState | "form";
  messageKey: string;
}

/** What a failed save means for the form; null for errors that were
 * announced as a snack. */
export const workspaceSaveRejection = (error: unknown): Rejection | null => {
  const code = getDocumentCraftingError(error)?.code;
  if (code === "model_not_accessible") {
    // The workspace does not grant the model (not the editing user).
    return { field: "default_model_id", messageKey: "settings.workspace.modelNotGranted" };
  }
  if (code !== "invalid_request") return null;
  const field = rejectedField(error);
  return field
    ? { field, messageKey: "settings.rejectedValue" }
    : { field: "form", messageKey: "settings.rejectedForm" };
};

const toForm = (settings: DocumentCraftingWorkspaceSettings): FormState => ({
  default_model_id: settings.default_model_id,
  company_phone: settings.company_phone,
  website_url: settings.website_url,
  english_variant: settings.english_variant,
  translation_marker: settings.translation_marker,
  draft_ttl_days: String(settings.draft_ttl_days),
  max_upload_mb: String(settings.max_upload_mb),
});

const isWholeNumberIn = (value: string, range: { min: number; max: number }) => {
  const number = Number(value);
  return (
    value.trim() !== "" &&
    Number.isInteger(number) &&
    number >= range.min &&
    number <= range.max
  );
};

const isWebUrl = (value: string) => {
  try {
    const url = new URL(value.trim());
    return (url.protocol === "https:" || url.protocol === "http:") && !!url.hostname;
  } catch {
    return false;
  }
};

/** Error keys (`settings.workspace.<key>`) per field. */
export const workspaceConfigErrors = (form: FormState): FieldErrors => {
  const errors: FieldErrors = {};
  if (form.company_phone.trim().length > COMPANY_PHONE_MAX) errors.company_phone = "tooLong";
  if (form.website_url.trim().length > WEBSITE_MAX) errors.website_url = "tooLong";
  else if (!isWebUrl(form.website_url)) errors.website_url = "websiteInvalid";
  if (!isWholeNumberIn(form.draft_ttl_days, DRAFT_TTL_RANGE)) {
    errors.draft_ttl_days = "range";
  }
  if (!isWholeNumberIn(form.max_upload_mb, MAX_UPLOAD_RANGE)) {
    errors.max_upload_mb = "range";
  }
  return errors;
};

/** Only the fields that differ from the saved settings. */
export const workspaceConfigUpdate = (
  settings: DocumentCraftingWorkspaceSettings,
  form: FormState,
): DocumentCraftingWorkspaceSettingsUpdate => {
  const update: DocumentCraftingWorkspaceSettingsUpdate = {};
  if (form.default_model_id !== settings.default_model_id) {
    update.default_model_id = form.default_model_id;
  }
  if (form.company_phone.trim() !== settings.company_phone) {
    update.company_phone = form.company_phone.trim();
  }
  if (form.website_url.trim() !== settings.website_url) {
    update.website_url = form.website_url.trim();
  }
  if (form.english_variant !== settings.english_variant) {
    update.english_variant = form.english_variant;
  }
  if (form.translation_marker !== settings.translation_marker) {
    update.translation_marker = form.translation_marker;
  }
  if (Number(form.draft_ttl_days) !== settings.draft_ttl_days) {
    update.draft_ttl_days = Number(form.draft_ttl_days);
  }
  if (Number(form.max_upload_mb) !== settings.max_upload_mb) {
    update.max_upload_mb = Number(form.max_upload_mb);
  }
  return update;
};

const ConfigFields: React.FC<{ settings: DocumentCraftingWorkspaceSettings }> = ({
  settings,
}) => {
  const { t } = useTranslation("documentCrafting");
  const models = useDocumentCraftingModels();
  const save = useUpdateDocumentCraftingWorkspaceSettings();
  const [form, setForm] = useState<FormState>(() => toForm(settings));
  // A save the server rejected, shown on the field it names.
  const [rejection, setRejection] = useState<Rejection | null>(null);
  const rejected = rejection?.field ?? null;
  const readOnly = !settings.can_manage;
  const disabled = readOnly || save.isPending;

  const errors = workspaceConfigErrors(form);
  const update = workspaceConfigUpdate(settings, form);
  const changed = Object.keys(update).length > 0;
  const valid = Object.keys(errors).length === 0;
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm({ ...form, [key]: value });
    if (rejected === key || rejected === "form") setRejection(null);
  };
  const errorText = (key: keyof FormState, limit?: { min?: number; max: number }) =>
    errors[key]
      ? t(`settings.workspace.${errors[key]}`, limit)
      : rejection && rejected === key
        ? t(rejection.messageKey)
        : undefined;
  const hasError = (key: keyof FormState) => Boolean(errors[key]) || rejected === key;

  // The models the workspace grants (what the server checks the default
  // against); the caller's own models only from a server without the list.
  const modelOptions = settings.available_models ?? models.data?.models ?? [];
  const unknownModel =
    form.default_model_id !== null &&
    !modelOptions.some((model) => model.model_id === form.default_model_id);
  const variants = ENGLISH_VARIANTS.includes(form.english_variant)
    ? ENGLISH_VARIANTS
    : [...ENGLISH_VARIANTS, form.english_variant];

  return (
    <Box
      component="form"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        if (readOnly || !changed || !valid) return;
        setRejection(null);
        save.mutate(update, {
          onError: (error) => setRejection(workspaceSaveRejection(error)),
        });
      }}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {readOnly && (
        <Alert severity="info">{t("settings.workspace.readOnly")}</Alert>
      )}
      {rejection && rejected === "form" && (
        <Alert severity="error">{t(rejection.messageKey)}</Alert>
      )}
      <TextField
        select
        size="small"
        label={t("settings.workspace.defaultModel")}
        value={form.default_model_id === null ? "" : String(form.default_model_id)}
        onChange={(event) =>
          set(
            "default_model_id",
            event.target.value === "" ? null : Number(event.target.value),
          )
        }
        disabled={disabled}
        error={hasError("default_model_id")}
        helperText={errorText("default_model_id") ?? t("settings.workspace.defaultModelHint")}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="">{t("settings.workspace.moduleDefault")}</MenuItem>
        {modelOptions.map((model) => (
          <MenuItem key={model.model_id} value={String(model.model_id)}>
            {model.model_name}
          </MenuItem>
        ))}
        {unknownModel && (
          <MenuItem value={String(form.default_model_id)}>
            {t("settings.workspace.unknownModel", { id: form.default_model_id })}
          </MenuItem>
        )}
      </TextField>
      <TextField
        size="small"
        label={t("settings.workspace.companyPhone")}
        value={form.company_phone}
        onChange={(event) => set("company_phone", event.target.value)}
        error={hasError("company_phone")}
        helperText={
          errorText("company_phone", { max: COMPANY_PHONE_MAX }) ??
          t("settings.workspace.companyPhoneHint")
        }
        disabled={disabled}
        inputProps={{ inputMode: "tel" }}
      />
      <TextField
        size="small"
        label={t("settings.workspace.websiteUrl")}
        value={form.website_url}
        onChange={(event) => set("website_url", event.target.value)}
        error={hasError("website_url")}
        helperText={
          errorText("website_url", { max: WEBSITE_MAX }) ?? t("settings.workspace.websiteUrlHint")
        }
        disabled={disabled}
        inputProps={{ inputMode: "url" }}
      />
      <TextField
        select
        size="small"
        label={t("settings.workspace.englishVariant")}
        value={form.english_variant}
        onChange={(event) => set("english_variant", event.target.value)}
        disabled={disabled}
        error={hasError("english_variant")}
        helperText={errorText("english_variant")}
      >
        {variants.map((variant) => (
          <MenuItem key={variant} value={variant}>
            {t(`settings.workspace.variants.${variant}`, { defaultValue: variant })}
          </MenuItem>
        ))}
      </TextField>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={form.translation_marker}
              onChange={(event) => set("translation_marker", event.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Typography sx={{ fontSize: 14, color: color.gray900 }}>
              {t("settings.workspace.translationMarker")}
            </Typography>
          }
        />
        <Typography sx={{ fontSize: 12, color: color.gray500, ml: 6 }}>
          {t("settings.workspace.translationMarkerHint")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <TextField
          size="small"
          type="number"
          label={t("settings.workspace.draftTtlDays")}
          value={form.draft_ttl_days}
          onChange={(event) => set("draft_ttl_days", event.target.value)}
          error={hasError("draft_ttl_days")}
          helperText={
            errorText("draft_ttl_days", DRAFT_TTL_RANGE) ??
            t("settings.workspace.draftTtlHint")
          }
          disabled={disabled}
          inputProps={{ min: DRAFT_TTL_RANGE.min, max: DRAFT_TTL_RANGE.max, step: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label={t("settings.workspace.maxUploadMb")}
          value={form.max_upload_mb}
          onChange={(event) => set("max_upload_mb", event.target.value)}
          error={hasError("max_upload_mb")}
          helperText={
            errorText("max_upload_mb", MAX_UPLOAD_RANGE) ??
            t("settings.workspace.maxUploadHint")
          }
          disabled={disabled}
          inputProps={{ min: MAX_UPLOAD_RANGE.min, max: MAX_UPLOAD_RANGE.max, step: 1 }}
        />
      </Box>
      {!readOnly && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            color="secondary"
            onClick={() => setForm(toForm(settings))}
            disabled={!changed || save.isPending}
          >
            {t("settings.workspace.reset")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!changed || !valid || save.isPending}
          >
            {t("common.save")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

/** "Workspace defaults": model, company phone, website, English variant,
 * translation marker, draft lifetime and upload limit (workspace admins). */
const WorkspaceConfigForm: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const settings = useDocumentCraftingWorkspaceSettings();
  const data = settings.data;

  return (
    <Panel
      title={t("settings.workspace.title")}
      subtitle={t("settings.workspace.subtitle")}
    >
      {data ? (
        // Remounts with the saved values.
        <ConfigFields key={JSON.stringify(data)} settings={data} />
      ) : settings.isError ? (
        <ErrorState onRetry={() => settings.refetch()} />
      ) : (
        <LoadingState />
      )}
    </Panel>
  );
};

export default WorkspaceConfigForm;
